import React from 'react';
import { View, Image, Linking, ImageSourcePropType } from 'react-native';
import styled from '@emotion/native';
import { H1, BodyText } from '@/shared/components/StyledText';
import { sand, secondaryHover } from '@/shared/styles/colors';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import type { EventConfig } from './constants';

interface HeroSectionProps {
  config: EventConfig['hero'];
  backgroundImage: ImageSourcePropType;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  config,
  backgroundImage,
}) => {
  const { isDesktop } = useBreakpoint();
  const { isHovered, webHoverProps } = useWebHoverToggle();

  const handleDownloadClick = async () => {
    try {
      await Linking.openURL(config.appStoreUrl);
    } catch (error) {
      console.error('Error opening app store URL:', error);
    }
  };

  return (
    <HeroSectionContainer>
      <HeroImage source={backgroundImage} />
      <HeroContent>
        <HeroTitle isDesktop={isDesktop}>
          {isDesktop
            ? config.title
            : config.mobileTitle.map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < config.mobileTitle.length - 1 && '\n'}
                </React.Fragment>
              ))}
        </HeroTitle>
        <DateText>
          <CalendarIcon
            source={require('@/assets/images/event-landing/CalIcon.png')}
          />
          <DateTextLabel>{config.dates}</DateTextLabel>
        </DateText>
        <DownloadButton
          accessibilityLabel="Download on the App Store"
          accessibilityRole="button"
          onPress={handleDownloadClick}
          {...webHoverProps}
          style={
            IS_WEB && isHovered
              ? { backgroundColor: secondaryHover }
              : undefined
          }
        >
          <AppStoreImage
            source={require('@/assets/images/event-landing/AppStore.png')}
          />
        </DownloadButton>
      </HeroContent>
    </HeroSectionContainer>
  );
};

export default HeroSection;

const HeroSectionContainer = styled(View)({
  position: 'relative',
  height: 600,
  minHeight: 600,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
});

const HeroImage = styled(Image)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0.6,
  zIndex: 1,
});

const HeroContent = styled(View)({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
  maxWidth: 650,
});

const HeroTitle = styled(H1)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 16,
  fontSize: isDesktop ? 36 : 30,
  color: sand,
}));

const DateText = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 24,
  gap: 8,
});

const DateTextLabel = styled(BodyText)({
  color: sand,
  fontSize: 14,
  fontWeight: 'bold',
  opacity: 0.9,
});

const CalendarIcon = styled(Image)({
  width: 16,
  height: 16,
});

const DownloadButton = styled.TouchableOpacity({
  backgroundColor: sand,
  borderRadius: 50,
  height: 70,
  width: 308,
  alignItems: 'center',
  justifyContent: 'center',
  ...BOX_SHADOW_STYLE,
});

const AppStoreImage = styled(Image)({
  width: 154,
  height: 36,
});
